import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import AsyncStorage from '@react-native-async-storage/async-storage';

interface User {
  id: string;
  display_name: string;
  created_at: string;
}

interface GuestUsage {
  messageCount: number;
  practiceCount: number;
  lastResetDate: string;
}

interface AuthState {
  user: User | null;
  isLoggedIn: boolean;
  isLoading: boolean;
  isGuestMode: boolean;
  guestUsage: GuestUsage;
}

const initialState: AuthState = {
  user: null,
  isLoggedIn: false,
  isLoading: true,
  isGuestMode: false,
  guestUsage: {
    messageCount: 0,
    practiceCount: 0,
    lastResetDate: new Date().toDateString(),
  },
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isLoggedIn = true;
      state.isLoading = false;
      state.isGuestMode = false;
    },
    logout: (state) => {
      state.user = null;
      state.isLoggedIn = false;
      state.isLoading = false;
      state.isGuestMode = false;
      // 清除本地存储的用户信息
      AsyncStorage.removeItem('userInfo').catch(console.error);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    enterGuestMode: (state) => {
      state.isGuestMode = true;
      state.isLoading = false;
      // 检查是否需要重置每日使用量
      const today = new Date().toDateString();
      if (state.guestUsage.lastResetDate !== today) {
        state.guestUsage.messageCount = 0;
        state.guestUsage.practiceCount = 0;
        state.guestUsage.lastResetDate = today;
      }
    },
    incrementGuestMessageCount: (state) => {
      if (state.isGuestMode) {
        state.guestUsage.messageCount += 1;
        // 保存到本地存储
        AsyncStorage.setItem('guestUsage', JSON.stringify(state.guestUsage)).catch(console.error);
      }
    },
    incrementGuestPracticeCount: (state) => {
      if (state.isGuestMode) {
        state.guestUsage.practiceCount += 1;
        // 保存到本地存储
        AsyncStorage.setItem('guestUsage', JSON.stringify(state.guestUsage)).catch(console.error);
      }
    },
    loadGuestUsage: (state, action: PayloadAction<GuestUsage>) => {
      state.guestUsage = action.payload;
    },
    restoreUserSession: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isLoggedIn = true;
      state.isLoading = false;
      state.isGuestMode = false;
    },
  },
});

export const {
  login,
  logout,
  setLoading,
  enterGuestMode,
  incrementGuestMessageCount,
  incrementGuestPracticeCount,
  loadGuestUsage,
  restoreUserSession,
} = authSlice.actions;

// 异步action用于持久化登录状态
export const saveUserSession = (user: User) => async (dispatch: any) => {
  try {
    await AsyncStorage.setItem('userInfo', JSON.stringify(user));
    dispatch(login(user));
  } catch (error) {
    console.error('Failed to save user session:', error);
  }
};

// 异步action用于恢复登录状态
export const loadUserSession = () => async (dispatch: any) => {
  try {
    const userInfo = await AsyncStorage.getItem('userInfo');
    const guestUsageStr = await AsyncStorage.getItem('guestUsage');

    if (guestUsageStr) {
      const guestUsage = JSON.parse(guestUsageStr);
      dispatch(loadGuestUsage(guestUsage));
    }

    if (userInfo) {
      const user = JSON.parse(userInfo);
      dispatch(restoreUserSession(user));
      return user;
    }
  } catch (error) {
    console.error('Failed to load user session:', error);
  }

  dispatch(setLoading(false));
  return null;
};

export default authSlice.reducer;
